import { IonBadge, IonCard, IonIcon, IonList, IonPopover } from '@ionic/react';
import { calendar, time } from 'ionicons/icons';
import React, { useMemo, useState } from 'react';
import './botScheduleCard.scss';
import TagList from '../customTagList/CustomTagList';
import { utilService } from '../../../../services';
import dayjs from 'dayjs';
import Call from '../../../../components/Shared/Call/call';
import { informationCircleOutline } from 'ionicons/icons';
import ChatbotCall from '../../../../components/Shared/Call/ChatbotCall';

interface Schedule {
  first_name: string;
  last_name: string;
  start_date: string;
  end_date: string;
  schedule_guid?: string;
}

interface UserSchedule {
  user?: {
    first_name?: string;
    last_name?: string;
  };
  schedules?: Schedule[];
}

interface BotScheduleCardProps {
  schedules: Schedule[]
}

const BotScheduleCard: React.FC<BotScheduleCardProps> = ({ schedules }) => {
  const [expandedDays, setExpandedDays] = useState<string[]>([]);
  const [showAllDays, setShowAllDays] = useState(false);
  const loggedInUserGuid = utilService.getLoggedUserGuid()

  const { groupedSchedules, totalDays, totalSchedules } = useMemo(() => {
    if (!Array.isArray(schedules)) {
      return { groupedSchedules: [], totalDays: 0, totalSchedules: 0 };
    }

    // Extract all schedule items
    const allSchedules = Array.isArray(schedules)
      ? schedules?.map(schedule => ({
        ...schedule,
        user: {
          first_name: schedule?.first_name,
          last_name: schedule?.last_name
        }
      }))
      : [];

    // Group by date
    const schedulesByDate = allSchedules?.reduce((groups, schedule) => {
      const startDate = new Date(schedule.start_date);
     const endDate = new Date(schedule.end_date);
const isSameDay = startDate.toDateString() === endDate.toDateString();

const dateStr = isSameDay
  ? startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  : `${startDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

      if (!groups[dateStr]) {
        groups[dateStr] = [];
      }

      groups[dateStr]?.push(schedule);
      return groups;
    }, {} as Record<string, any[]>);

    // Convert to array and sort by date
    const groupedSchedules = Object.entries(schedulesByDate)
      .map(([date, items]) => ({ date, items }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return {
      groupedSchedules,
      totalDays: groupedSchedules.length,
      totalSchedules: allSchedules.length
    };
  }, [schedules]);

  // Format time for display
  const formatTime = (timeString: string) => {
    return dayjs(timeString).format('h:mm A');
  };

  // Handle error or empty schedules
  if (!Array.isArray(schedules) || schedules?.length === 0) {
    return (
      <IonCard className="bot-schedule-card">
        <div className="no-schedules-text">
          {typeof schedules === 'string' ? schedules : 'No schedules available'}
        </div>
      </IonCard>
    );
  }

  // Toggle expanded state for a specific day
  const toggleDayExpansion = (date: string) => {
    setExpandedDays(prev =>
      prev.includes(date)
        ? prev.filter(d => d !== date)
        : [...prev, date]
    );
  };

  // Toggle showing all days
  const toggleShowAllDays = () => {
    setShowAllDays(prev => !prev);
  };

  return (
    <IonCard className="bot-schedule-card">
      <div className="header-container">
        <div className="header-left">
          <IonIcon icon={calendar} className="header-icon" />
          <span className="header-title">Schedules&nbsp;{"  "}</span>
        </div>
        <span className="summary-text">
          {totalSchedules} schedule{totalSchedules !== 1 ? 's' : ''} • {totalDays} day{totalDays !== 1 ? 's' : ''}
        </span>
      </div>

      <IonList className="schedule-list">
        {(showAllDays ? groupedSchedules : groupedSchedules.slice(0, 3)).map((item, index) => {
          const isExpanded = expandedDays.includes(item.date);
          const displayItems = isExpanded ? item.items : item.items.slice(0, 2);
          const hasMore = item.items.length > 2 && !isExpanded;

          return (
            <div
              key={item.date}
              className={`date-section ${index !== (showAllDays ? groupedSchedules.length : Math.min(groupedSchedules.length, 3)) - 1 ? 'with-border' : ''}`}
            >
              <div className="date-header">
                <span className="date-text">{item.date}</span>
                <IonBadge className="count-badge">{item.items.length}</IonBadge>
              </div>

              {displayItems.map((schedule, scheduleIndex) => (
                <div key={`${schedule.id}-${scheduleIndex}`} className="schedule-item">

                  {schedule.user && (schedule.user.first_name || schedule.user.last_name) && (
                    <div className="user-name-row">
                      <span className="user-name-text">
                        {`${schedule.user.first_name || ''} ${schedule.user.last_name || ''}`.trim()}
                      </span>
                      {schedule.guid && <ChatbotCall   disabled={loggedInUserGuid === schedule.guid? true:false}  selectedDoctorGuid={schedule.guid} />}
                    </div>

                  )}

                  <div className="time-container">
                    <IonIcon icon={time} className="time-icon" />
                    <span className="schedule-time">
                      {formatTime(schedule.start_date)} - {formatTime(schedule.end_date)}
                    </span>
                    {schedule?.change_status_name === 'REQUEST PENDING' && (

                      <div className="pending-dot-tooltip-wrapper">
                        <div className="pending-dot"><IonIcon style={{color: '#a16b0e'}}
                          icon={informationCircleOutline} /></div>
                        <span className="tooltip-text">This shift is pending</span>
                      </div>
                    )}
                  </div>

                  <TagList
                    items={utilService.parseStringToArray(schedule?.specialisation_names)}
                    type="specialization"
                    maxVisibleItems={2}
                    size="small"
                    showIcon={true}
                    expandable={true}
                  />


                </div>
              ))}

              {hasMore && (
                <div
                  className="more-indicator"
                  onClick={() => toggleDayExpansion(item.date)}
                >
                  + {item.items.length - 2} more
                </div>
              )}

              {isExpanded && item.items.length > 2 && (
                <div
                  className="less-indicator"
                  onClick={() => toggleDayExpansion(item.date)}
                >
                  Show less
                </div>
              )}
            </div>
          );
        })}
      </IonList>

      {groupedSchedules.length > 3 && !showAllDays ? (
        <div className="more-schedules" onClick={toggleShowAllDays}>
          + {groupedSchedules.length - 3} more days
        </div>
      ) : groupedSchedules.length > 3 && showAllDays ? (
        <div className="more-schedules" onClick={toggleShowAllDays}>
          Show less
        </div>
      ) : null}
    </IonCard>
  );
};

export default BotScheduleCard;